import { openDB, type IDBPDatabase } from 'idb';
import { getActiveTenantId, getActiveUserId } from '@/lib/active-tenant';

const DB_NAME = 'efacture-offline';
// v2: `tenantId` added to sync-queue entries.
// v3: reference-snapshot keys became tenant-scoped; the unscoped ones are
// dropped. See the upgrade callback.
const DB_VERSION = 3;

export interface SyncQueueEntry {
  /**
   * The `Idempotency-Key` of the first attempt (minted in client.ts's
   * `fetchApi`), resent unchanged by every replay (sync-queue.ts).
   */
  id: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  /**
   * The headers the caller added to the first attempt (e.g. the
   * X-SFE-Fingerprint of the bundle that queued a till sale), sent again by
   * every replay. Absent when the caller added none.
   */
  headers?: Readonly<Record<string, string>>;
  timestamp: number;
  retryCount: number;
  /**
   * 'pending' (default, also for legacy entries without the field):
   * eligible for automatic sync. 'error': permanent failure (4xx other
   * than 408/429, or retries exhausted) — kept for manual review
   * (retry/discard) instead of being silently dropped.
   */
  state?: 'pending' | 'error';
  /** French, user-facing — set when state === 'error'. */
  errorMessage?: string;
  /** HTTP status that caused the failure, when known. */
  errorStatus?: number | undefined;
  /**
   * Tenant this mutation was queued under: the dossier the request was
   * sent for when the caller names it, else the `tenantId` claim of the
   * access token in force at enqueue time. Stamped by `addToSyncQueue`,
   * which falls back to the token itself, so an entry is never left
   * without one by a caller that forgot it.
   *
   * `null` (or absent, on records written before DB v2) means
   * UNATTRIBUTABLE: the queue may belong to a previous user, so no tenant
   * can be assumed. Such entries are never replayed automatically — see
   * `isHeldEntry`.
   */
  tenantId?: string | null;
  /**
   * User who queued this mutation: the `userId` claim of the access token
   * in force at enqueue time, stamped by `addToSyncQueue`. A replay carries
   * the token of whoever is logged in then, and the API records THAT user
   * as the actor, so the entry is only replayed under its own user.
   *
   * `null` (or absent, on records written before this field) means
   * UNATTRIBUTABLE to a user: never replayed automatically — see
   * `isHeldEntry`.
   */
  userId?: string | null;
}

/** The session a queue entry is checked against: this tab's token claims. */
export interface ActiveSession {
  tenantId: string | null;
  userId: string | null;
}

/** Claims of this tab's token, read together from the same token. */
export function getActiveSession(): ActiveSession {
  return { tenantId: getActiveTenantId(), userId: getActiveUserId() };
}

export interface QueueCounts {
  pending: number;
  failed: number;
  /** Queued under another tenant or user, or unattributable — see isHeldEntry. */
  held: number;
}

interface CachedReference {
  key: string;
  data: unknown;
  cachedAt: number;
}

// NOTE: the 'draft-invoices' store (offline invoice drafts) was removed
// 2026-07-02 — it had zero consumers (dead code). Databases created
// before that date keep an empty orphan store; harmless.
export interface OfflineDB {
  'sync-queue': {
    key: string;
    value: SyncQueueEntry;
    indexes: { 'by-timestamp': number };
  };
  'cached-data': {
    key: string;
    value: CachedReference;
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<OfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          // Sync queue for offline mutations
          const syncStore = db.createObjectStore('sync-queue', { keyPath: 'id' });
          syncStore.createIndex('by-timestamp', 'timestamp');

          // Cached reference data (articles, clients)
          db.createObjectStore('cached-data', { keyPath: 'key' });
        }

        if (oldVersion >= 1 && oldVersion < 2) {
          // Entries already queued when tenant tagging shipped carry no
          // tenant. They are NOT tagged with whoever happens to be logged
          // in during the migration — that is precisely the wrong
          // assumption, since the queue may belong to a previous user.
          // They are marked UNATTRIBUTABLE and surfaced for review.
          // (The permanent defence is the read-time guard in
          // `isHeldEntry`; this migration only makes the data explicit,
          // and it runs once.)
          let cursor = await tx.objectStore('sync-queue').openCursor();
          while (cursor) {
            if (cursor.value.tenantId === undefined) {
              await cursor.update({ ...cursor.value, tenantId: null });
            }
            cursor = await cursor.continue();
          }
        }

        if (oldVersion >= 1 && oldVersion < 3) {
          // Snapshots written before tenant scoping sit under unscoped
          // keys ('ref:articles', 'ref:clients') and cannot be attributed
          // to a tenant. They are DELETED, not held.
          //
          // This is deliberately the opposite of the v2 sync-queue
          // decision, and for a reason that is not a matter of taste: the
          // queue holds the user's OWN unsynced work, which nothing else
          // can reconstruct, so losing it silently was unacceptable and
          // legacy entries were kept for review. This store holds a COPY
          // of server data — articles and clients are re-fetched the
          // moment the device is online — so discarding costs nothing and
          // keeping would be the only risk. Do not "harmonise" the two.
          //
          // The permanent guarantee is not this migration (it runs once):
          // `referenceCacheKey` can only ever produce
          // `ref:<tenant>:<kind>`, so a two-segment legacy key is
          // unreachable by construction. Clearing merely reclaims space.
          // Nothing tenant-scoped can exist before v3, so `clear()` is
          // exact.
          await tx.objectStore('cached-data').clear();
        }
      },
    });
  }
  return dbPromise;
}

// ─── Sync Queue Operations ───

// Callback invoked after queue changes (add/remove/mark-error) with new
// counts. Registered by sync-queue.ts to bridge notifications to
// OfflineContext.
let onQueueChanged: ((counts: QueueCounts) => void) | null = null;

export function setQueueChangeCallback(cb: (counts: QueueCounts) => void) {
  onQueueChanged = cb;
}

async function notifyQueueChanged() {
  if (onQueueChanged) {
    onQueueChanged(await getQueueCounts());
  }
}

export function isFailedEntry(entry: SyncQueueEntry): boolean {
  return entry.state === 'error';
}

/**
 * Entry whose originating tenant is unknown — written before DB v2, or
 * queued with no session. No tenant can be assumed for it, ever.
 */
export function isUnattributableEntry(entry: SyncQueueEntry): boolean {
  return entry.tenantId === undefined || entry.tenantId === null;
}

/**
 * Entry whose originating user is unknown — written before entries carried
 * a user, or queued with no session. No user can be assumed for it, ever.
 */
export function isUserUnattributableEntry(entry: SyncQueueEntry): boolean {
  return entry.userId === undefined || entry.userId === null;
}

/**
 * Entry that MUST NOT be replayed under the active session.
 *
 * The queue replays through `apiClient`, which carries whatever token is
 * current — so without this guard a mutation queued offline by tenant A
 * is sent under tenant B's session and creates records in the wrong
 * tenant (I6), and one queued by user A is sent under user B's session,
 * which records B as its actor and checks B's permissions. Holding is not
 * discarding: the entry stays in the queue, is surfaced in the UI, and
 * leaves on its own the moment its tenant and its user are active again.
 *
 * Failed entries are excluded: they already have their own manual-review
 * path and must not be re-labelled.
 */
export function isHeldEntry(entry: SyncQueueEntry, active: ActiveSession): boolean {
  if (isFailedEntry(entry)) return false;
  if (isUnattributableEntry(entry) || isUserUnattributableEntry(entry)) return true;
  return entry.tenantId !== active.tenantId || entry.userId !== active.userId;
}

/**
 * `tenantId` is the dossier the request was sent for, and `userId` the user
 * of the token it carried. The token in force is read in their place when
 * they are not given; another tab may have switched it, or another user may
 * have logged in, since the request was sent, which is why `apiClient`
 * passes both.
 */
export async function addToSyncQueue(
  entry: Omit<SyncQueueEntry, 'timestamp' | 'retryCount' | 'state' | 'tenantId' | 'userId'>,
  tenantId: string | null = getActiveTenantId(),
  userId: string | null = getActiveUserId()
): Promise<string> {
  const db = await getDB();
  const record: SyncQueueEntry = {
    ...entry,
    timestamp: Date.now(),
    retryCount: 0,
    state: 'pending',
    tenantId,
    userId,
  };
  await db.put('sync-queue', record);
  await notifyQueueChanged();
  return record.id;
}

export async function getSyncQueue(): Promise<SyncQueueEntry[]> {
  const db = await getDB();
  return db.getAllFromIndex('sync-queue', 'by-timestamp');
}

export async function removeSyncQueueEntry(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('sync-queue', id);
  await notifyQueueChanged();
}

export async function incrementRetry(id: string): Promise<void> {
  const db = await getDB();
  const entry = await db.get('sync-queue', id);
  if (entry) {
    entry.retryCount += 1;
    await db.put('sync-queue', entry);
  }
}

/**
 * Mark an entry as permanently failed — kept in the queue for manual
 * review (retry/discard) instead of being deleted silently.
 */
export async function markSyncQueueEntryError(
  id: string,
  errorMessage: string,
  errorStatus?: number
): Promise<void> {
  const db = await getDB();
  const entry = await db.get('sync-queue', id);
  if (entry) {
    entry.state = 'error';
    entry.errorMessage = errorMessage;
    entry.errorStatus = errorStatus;
    await db.put('sync-queue', entry);
    await notifyQueueChanged();
  }
}

/** Reset a failed entry so the next sync pass retries it. */
export async function resetSyncQueueEntry(id: string): Promise<void> {
  const db = await getDB();
  const entry = await db.get('sync-queue', id);
  if (entry) {
    entry.state = 'pending';
    entry.retryCount = 0;
    delete entry.errorMessage;
    delete entry.errorStatus;
    await db.put('sync-queue', entry);
    await notifyQueueChanged();
  }
}

export async function getQueueCounts(): Promise<QueueCounts> {
  const entries = await getSyncQueue();
  const active = getActiveSession();
  let pending = 0;
  let failed = 0;
  let held = 0;
  for (const entry of entries) {
    if (isFailedEntry(entry)) failed++;
    else if (isHeldEntry(entry, active)) held++;
    else pending++;
  }
  return { pending, failed, held };
}

export async function getSyncQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count('sync-queue');
}

// ─── Cached Reference Data ───

/**
 * The reference datasets cached for offline use: the article and client
 * lists `useOfflineCache` snapshots, the payment methods the till
 * offers (`fetchPosPaymentMethods`) and the nav entries the company
 * switched off (`fetchNavPreferences`). Callers name a
 * KIND, never a storage key: the key is built below, from the access
 * token's tenant claim, which makes an unscoped read or write
 * unexpressible rather than merely discouraged.
 *
 * Same discipline as `addToSyncQueue`, which stamps `tenantId` itself
 * and removes it from its input type — a discriminator a caller must
 * remember to supply is a discriminator that will one day be left out.
 */
export type ReferenceCacheKind =
  | 'articles'
  | 'clients'
  | 'payment-methods'
  | 'nav-disabled'
  | 'dashboard-widgets'
  | 'dashboard-widgets-accounting'
  | 'dashboard-widgets-firm'
  | 'dashboard-widgets-stock'
  // `table-columns:<userId>:<tableId>`: a user's column choice, kept per tenant and user.
  | `table-columns:${string}:${string}`;

/**
 * Storage key of a snapshot for the session in force, or `null` when
 * there is no usable tenant claim.
 *
 * Scoping the KEY, not just the read, is what closes the gap: React
 * Query's cache identity is already tenant-scoped (`hashQueryKeyByTenant`
 * in `@/lib/query-client`), so a cross-tenant cache HIT is impossible —
 * but hydration writes through `setQueryData`, which injects whatever it
 * is handed straight into the active tenant's cache. Reading tenant A's
 * rows and handing them to tenant B's `setQueryData` walks around the
 * scoping instead of through it. With the tenant inside the key, B's
 * read simply finds nothing.
 */
function referenceCacheKey(kind: ReferenceCacheKind): string | null {
  const tenantId = getActiveTenantId();
  return tenantId ? `ref:${tenantId}:${kind}` : null;
}

export async function cacheReferenceData(
  kind: ReferenceCacheKind,
  data: unknown
): Promise<void> {
  const key = referenceCacheKey(kind);
  // No session, or a token with no usable claim: the snapshot cannot be
  // attributed to a tenant, so it is not stored at all. An unattributable
  // record here would be exactly the legacy entry v3 exists to delete.
  if (!key) return;
  const db = await getDB();
  await db.put('cached-data', { key, data, cachedAt: Date.now() });
}

export async function getCachedReferenceData<T>(
  kind: ReferenceCacheKind
): Promise<{ data: T; cachedAt: number } | undefined> {
  const key = referenceCacheKey(kind);
  if (!key) return undefined;
  const db = await getDB();
  const record = await db.get('cached-data', key);
  if (!record) return undefined;
  return { data: record.data as T, cachedAt: record.cachedAt };
}

/**
 * Drop every cached reference snapshot, whatever its tenant.
 *
 * Defence in depth, not the primary defence: keys carry their tenant
 * since v3, so the next session cannot read this one's snapshots even if
 * this purge never runs — which is the case an unclean exit (killed tab,
 * crash, power cut) produces. Emptying the store on an ORDERLY logout
 * still costs nothing and leaves no stale copy of server data on the
 * device. The sync queue is deliberately NOT touched: it holds the
 * user's own unsynced mutations.
 */
export async function clearCachedReferenceData(): Promise<void> {
  const db = await getDB();
  await db.clear('cached-data');
}
